/**
 * Admin & PM safe responses utility
 * Centralizes all privileged response queries to prevent RLS issues in test environments.
 * The underlying RPC allows both admins (all assessments) and portfolio managers
 * (only assessments in their portfolios) to fetch response data.
 */

import { supabase } from "@/integrations/supabase/client";

export interface AdminResponseRow {
  question_id: number;
  score: number | null;
  comment: string | null;
  evidence_links: string[] | null;
  updated_at: string;
}

/**
 * Fetch responses for an assessment using admin RPC (bypasses RLS)
 * @param assessmentId - The assessment UUID
 * @param questionIds - Optional array of question IDs to filter
 */
export async function fetchAdminResponses(
  assessmentId: string,
  questionIds: number[] | null = null
): Promise<AdminResponseRow[]> {
  const params: { p_assessment_id: string; p_question_ids?: number[] | null } = {
    p_assessment_id: assessmentId,
    p_question_ids: questionIds && questionIds.length > 0 ? questionIds.map(Number) : null,
  };
  
  // Log before RPC call
  console.log("[admin_list_responses] payload", { 
    p_assessment_id: params.p_assessment_id, 
    p_question_ids: params.p_question_ids 
  });
  
  // Use canonical admin_list_responses RPC
  const { data, error } = await (supabase as any).rpc("admin_list_responses", params);

  // Log after RPC call
  console.log("[admin_list_responses] result", { 
    error, 
    rows: data?.length 
  });

  if (error) {
    console.error("[ADMIN RESPONSES] RPC failed:", error);
    throw error;
  }

  // Filter by questionIds if provided, normalize evidence_links from jsonb to string[]
  let rows = (data ?? []).map((row: any) => ({
    question_id: row.question_id,
    score: row.score,
    comment: row.comment,
    evidence_links: Array.isArray(row.evidence_links) ? row.evidence_links : [],
    updated_at: row.updated_at,
  }));

  if (questionIds && questionIds.length > 0) {
    rows = rows.filter((r: AdminResponseRow) => questionIds.includes(r.question_id));
  }

  return rows;
}

/**
 * Get count of answered questions (score not null)
 */
export async function fetchAdminAnsweredCount(
  assessmentId: string
): Promise<number> {
  const responses = await fetchAdminResponses(assessmentId, null);
  return responses.filter((r) => r.score !== null && r.score !== undefined).length;
}

/**
 * Get count of evidence items
 */
export async function fetchAdminEvidenceCount(
  assessmentId: string
): Promise<number> {
  const responses = await fetchAdminResponses(assessmentId, null);
  return responses.reduce(
    (sum, r) => sum + (r.evidence_links?.length ?? 0),
    0
  );
}

/**
 * Get the latest response update timestamp
 */
export async function fetchAdminLastResponseAt(
  assessmentId: string
): Promise<string | null> {
  const responses = await fetchAdminResponses(assessmentId, null);
  if (!responses.length) return null;

  const latest = responses.reduce((max, r) => {
    if (!r.updated_at) return max;
    if (!max) return r.updated_at;
    return new Date(r.updated_at) > new Date(max) ? r.updated_at : max;
  }, null as string | null);

  return latest;
}
